import { SettingsManager } from "@viamap/viamap2-common";
import { ESInterfaceJWTAuth } from "src/compentsModus/ESInterfaceJWTAuth";
import { JPBManager } from "./JPBManager";
import JPBRegions from "./JPBRegions.json";
import {
  JPBExpFit,
  JPBIndexBuilderOptions,
  JPBIndexDocument,
  JPBIndexInterval,
  JPBIndexPeriod,
  JPBIndexSmoothingMode,
  JPBIndexStatistic,
} from "./JPBIndexBuilderTypes";

type MunicipalityItem = {
  landsdelNr: number;
  landsdel: string;
  kommunenr: number;
  kommunenavn: string;
};

type JPBSource = {
  dato?: number;
  pris?: number;
  m2total?: number;
  komkode?: string | number;
  prisDriftsbygning?: number;
  prisStuehus?: number;
};

type JPBHit = {
  _source?: JPBSource;
  sort?: unknown[];
  _seq_no?: number;
};

type PeriodTemplate = {
  key: string;
  start: number;
  end: number;
};

type ComputeAllProgress = {
  landsdel: string;
  current: number;
  total: number;
};

type JPBRawTransactionRow = {
  dato: number;
  pris: number;
  m2total: number;
  komkode: string;
  landsdel: string;
  prisDriftsbygning: number | null;
  prisStuehus: number | null;
};

type JPBRowsCache = {
  rows: JPBRawTransactionRow[];
  sourceIndexName: string;
  yearsBack: number;
  referenceDateMs: number;
  referenceFromMetadata: boolean;
  fromTimestamp: number;
  toTimestamp: number;
  fetchedAt: number;
};

export type JPBIndexBuilderCacheInfo = {
  usedCache: boolean;
  sourceIndexName: string;
  yearsBack: number;
  referenceDateMs: number;
  rowsInMemory: number;
  fromTimestamp: number;
  toTimestamp: number;
  fetchedAt: number;
};

export type JPBIndexBuilderComputeAllResult = {
  documents: JPBIndexDocument[];
  cache: JPBIndexBuilderCacheInfo;
};

export type JPBIndexBuilderRawCheckedRow = {
  landsdel: string;
  komkode: string;
  datoMs: number;
  periodKey: string;
  m2total: number;
  rawSalePrice: number;
  rawPricePerM2: number;
  prisDriftsbygning: number | null;
  prisStuehus: number | null;
  hasBuildingValue: boolean;
  buildingAreaThresholdHa: number;
  subtractBuildingPrices: boolean;
  effectiveRawPrice: number;
  effectivePricePerM2: number;
};

const DEFAULT_TARGET_INDEX = "JPBIndex";
const DEFAULT_YEARS_BACK = 5;
const MAX_PAGES = 5000;
const DEFAULT_FETCH_SIZE = 8000;
const MAX_FETCH_SIZE = 10000;

export class JPBIndexBuilderManager {
  private static rowsCache: JPBRowsCache | null = null;
  private static kommuneToLandsdelMap: Map<string, string> | null = null;

  static getLandsdelList(): string[] {
    const municipalities = (JPBRegions.municipalities || []) as MunicipalityItem[];
    const sorted = [...municipalities].sort((a, b) => {
      if (a.landsdelNr !== b.landsdelNr) return a.landsdelNr - b.landsdelNr;
      return a.landsdel.localeCompare(b.landsdel, "da");
    });
    const unique = new Set<string>();
    sorted.forEach((m) => unique.add(m.landsdel));
    return [...unique];
  }

  static getTargetIndexName(): string {
    return SettingsManager.getSystemSetting("jpb.indexBuilderIndex", DEFAULT_TARGET_INDEX);
  }

  static invalidateRowsCache(): void {
    this.rowsCache = null;
  }

  static buildDocumentId(
    doc: Pick<
      JPBIndexDocument,
      | "landsdel"
      | "interval"
      | "statistic"
      | "smoothingMode"
      | "buildingAreaThresholdHa"
      | "subtractBuildingPrices"
    >
  ): string {
    const base = `${doc.landsdel}__${doc.interval}__${doc.statistic}`.replace(/\s+/g, "_");
    const suffixes: string[] = [];
    if (doc.smoothingMode !== "none") suffixes.push(`sm-${doc.smoothingMode}`);
    if (doc.buildingAreaThresholdHa > 0) suffixes.push(`ba-${doc.buildingAreaThresholdHa.toString().replace(".", "_")}ha`);
    if (doc.subtractBuildingPrices) suffixes.push("sb-1");
    if (!suffixes.length) return base;
    return `${base}__${suffixes.join("__")}`;
  }

  static async computeDocument(options: JPBIndexBuilderOptions): Promise<JPBIndexDocument> {
    const yearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const { rows, cache } = await this.getOrFetchRawRows(yearsBack, false);
    const rowsByLandsdel = this.groupRowsByLandsdel(rows);
    return this.buildDocumentFromRows(
      options.landsdel,
      rowsByLandsdel.get(options.landsdel) || [],
      cache.fromTimestamp,
      cache.toTimestamp,
      options
    );
  }

  static async computeAllDocuments(
    options: Omit<JPBIndexBuilderOptions, "landsdel">,
    onProgress?: (progress: ComputeAllProgress) => void,
    runOptions?: { forceRefresh?: boolean }
  ): Promise<JPBIndexBuilderComputeAllResult> {
    const yearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const { rows, cache, usedCache } = await this.getOrFetchRawRows(yearsBack, Boolean(runOptions?.forceRefresh));
    const rowsByLandsdel = this.groupRowsByLandsdel(rows);
    const landsdele = this.getLandsdelList();
    const docs: JPBIndexDocument[] = [];

    for (let i = 0; i < landsdele.length; i++) {
      const landsdel = landsdele[i];
      onProgress?.({ landsdel, current: i + 1, total: landsdele.length });
      const doc = this.buildDocumentFromRows(
        landsdel,
        rowsByLandsdel.get(landsdel) || [],
        cache.fromTimestamp,
        cache.toTimestamp,
        { ...options, landsdel, yearsBack }
      );
      docs.push(doc);
    }

    return { documents: docs, cache: this.toCacheInfo(cache, usedCache) };
  }

  static async getRawCheckedRows(
    options: Omit<JPBIndexBuilderOptions, "landsdel"> & { landsdele?: string[] },
    runOptions?: { forceRefresh?: boolean }
  ): Promise<{ rows: JPBIndexBuilderRawCheckedRow[]; cache: JPBIndexBuilderCacheInfo }> {
    const yearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const { rows, cache, usedCache } = await this.getOrFetchRawRows(yearsBack, Boolean(runOptions?.forceRefresh));

    const selectedLandsdele =
      options.landsdele && options.landsdele.length > 0 ? new Set(options.landsdele) : null;
    const thresholdHa = Math.max(0, Number(options.buildingAreaThresholdHa) || 0);
    const thresholdM2 = thresholdHa * 10000;
    const subtractBuildingPrices = Boolean(options.subtractBuildingPrices);
    const out: JPBIndexBuilderRawCheckedRow[] = [];

    rows.forEach((row) => {
      if (selectedLandsdele && !selectedLandsdele.has(row.landsdel)) return;
      if (!(row.m2total > 0) || !(row.pris > 0)) return;

      const hasBuildingValue =
        (typeof row.prisDriftsbygning === "number" && row.prisDriftsbygning > 0) ||
        (typeof row.prisStuehus === "number" && row.prisStuehus > 0);
      const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || row.m2total >= thresholdM2;
      if (!buildingAreaOk) return;

      const buildingsPriceSum = (row.prisDriftsbygning ?? 0) + (row.prisStuehus ?? 0);
      const effectiveRawPrice = subtractBuildingPrices ? Math.max(0, row.pris - buildingsPriceSum) : row.pris;
      if (!(effectiveRawPrice > 0)) return;

      out.push({
        landsdel: row.landsdel,
        komkode: row.komkode,
        datoMs: row.dato,
        periodKey: this.toPeriodKey(new Date(row.dato), options.interval),
        m2total: row.m2total,
        rawSalePrice: row.pris,
        rawPricePerM2: row.pris / row.m2total,
        prisDriftsbygning: row.prisDriftsbygning,
        prisStuehus: row.prisStuehus,
        hasBuildingValue,
        buildingAreaThresholdHa: thresholdHa,
        subtractBuildingPrices,
        effectiveRawPrice,
        effectivePricePerM2: effectiveRawPrice / row.m2total,
      });
    });

    return { rows: out, cache: this.toCacheInfo(cache, usedCache) };
  }

  static async ensureTargetIndexExists(indexName?: string): Promise<string> {
    const targetIndex = indexName || this.getTargetIndexName();
    const es = new ESInterfaceJWTAuth(targetIndex);
    try {
      await es.createIndex(targetIndex, {
        mappings: {
          properties: {
            landsdel: { type: "keyword" },
            interval: { type: "keyword" },
            statistic: { type: "keyword" },
            smoothingMode: { type: "keyword" },
            buildingAreaThresholdHa: { type: "double" },
            subtractBuildingPrices: { type: "boolean" },
            yearsBack: { type: "integer" },
            generatedAt: { type: "long" },
            basePeriod: { type: "keyword" },
            periods: {
              type: "nested",
              properties: {
                periodKey: { type: "keyword" },
                periodStart: { type: "long" },
                periodEnd: { type: "long" },
                value: { type: "double" },
                factor: { type: "double" },
                txCount: { type: "integer" },
              },
            },
            fitExp: {
              properties: {
                a: { type: "double" },
                b: { type: "double" },
                rSquared: { type: "double" },
              },
            },
          },
        },
      });
    } catch (error: any) {
      const msg = `${error?.message || error || ""}`.toLowerCase();
      if (!msg.includes("already_exists")) {
        throw error;
      }
    }
    return targetIndex;
  }

  static async uploadDocuments(documents: JPBIndexDocument[], indexName?: string): Promise<{ indexName: string; uploaded: number }> {
    if (!documents.length) {
      return { indexName: indexName || this.getTargetIndexName(), uploaded: 0 };
    }

    const targetIndex = await this.ensureTargetIndexExists(indexName);
    const es = new ESInterfaceJWTAuth(targetIndex);
    const ndjson = this.toExportNDJSON(documents, targetIndex);
    const res = await es.bulk(targetIndex, ndjson);
    if (res?.errors) {
      const firstErrors = (res.items || [])
        .map((item: any) => item?.index?.error?.reason)
        .filter((reason: string | undefined) => Boolean(reason))
        .slice(0, 3);
      throw new Error(`Bulk upload completed with errors: ${firstErrors.join(" | ")}`);
    }
    return { indexName: targetIndex, uploaded: documents.length };
  }

  static toExportJSON(documents: JPBIndexDocument[]): string {
    const enriched = documents.map((doc) => {
      const idxByPeriodKey = new Map(doc.periods.map((period, idx) => [period.periodKey, idx]));
      const baseIdx = doc.basePeriod ? idxByPeriodKey.get(doc.basePeriod) : 0;
      const basePeriodValue =
        (doc.basePeriod
          ? doc.periods.find((period) => period.periodKey === doc.basePeriod)?.value
          : doc.periods.find((period) => typeof period.value === "number" && period.value > 0)?.value) ?? null;
      const baseFitFactor =
        doc.fitExp && baseIdx !== undefined
          ? doc.fitExp.a * Math.exp(doc.fitExp.b * baseIdx)
          : null;

      const periods = doc.periods.map((period, idx) => {
        const fitFactor = doc.fitExp ? doc.fitExp.a * Math.exp(doc.fitExp.b * idx) : null;
        const fitValue =
          typeof fitFactor === "number" &&
            Number.isFinite(fitFactor) &&
            typeof baseFitFactor === "number" &&
            Number.isFinite(baseFitFactor) &&
            baseFitFactor > 0 &&
            typeof basePeriodValue === "number" &&
            Number.isFinite(basePeriodValue) &&
            basePeriodValue > 0
            ? (fitFactor / baseFitFactor) * basePeriodValue
            : null;
        return {
          ...period,
          fitFactor,
          fitValue,
        };
      });

      return {
        ...doc,
        periods,
      };
    });

    return JSON.stringify(enriched, null, 2);
  }

  static toExportNDJSON(documents: JPBIndexDocument[], indexName?: string): string {
    const target = indexName || this.getTargetIndexName();
    const lines: string[] = [];
    documents.forEach((doc) => {
      lines.push(JSON.stringify({ index: { _index: target, _id: this.buildDocumentId(doc) } }));
      lines.push(JSON.stringify(doc));
    });
    return lines.join("\n") + "\n";
  }

  static computeExponentialFit(periods: JPBIndexPeriod[]): JPBExpFit | undefined {
    const points = periods
      .map((period, idx) => ({ x: idx, y: period.factor }))
      .filter((point) => typeof point.y === "number" && point.y > 0) as { x: number; y: number }[];

    if (points.length < 2) return undefined;

    const xMean = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const yLn = points.map((p) => Math.log(p.y));
    const yMean = yLn.reduce((sum, value) => sum + value, 0) / yLn.length;

    let numerator = 0;
    let denominator = 0;
    for (let i = 0; i < points.length; i++) {
      numerator += (points[i].x - xMean) * (yLn[i] - yMean);
      denominator += Math.pow(points[i].x - xMean, 2);
    }
    if (denominator === 0) return undefined;

    const b = numerator / denominator;
    const lnA = yMean - b * xMean;
    const a = Math.exp(lnA);

    let ssRes = 0;
    let ssTot = 0;
    for (let i = 0; i < points.length; i++) {
      const predicted = lnA + b * points[i].x;
      ssRes += Math.pow(yLn[i] - predicted, 2);
      ssTot += Math.pow(yLn[i] - yMean, 2);
    }
    const rSquared = ssTot <= 0 ? 1 : Math.max(0, Math.min(1, 1 - ssRes / ssTot));

    return { a, b, rSquared };
  }

  private static async getReferenceDateInfo(): Promise<{ date: Date; fromMetadata: boolean }> {
    const metadata = await JPBManager.fetchDatabaseMetadata().catch(() => null);
    if (metadata?.uploadedAt) {
      return { date: new Date(metadata.uploadedAt), fromMetadata: true };
    }
    return { date: new Date(), fromMetadata: false };
  }

  private static getKommuneToLandsdelMap(): Map<string, string> {
    if (this.kommuneToLandsdelMap) {
      return this.kommuneToLandsdelMap;
    }
    const municipalities = (JPBRegions.municipalities || []) as MunicipalityItem[];
    const map = new Map<string, string>();
    municipalities.forEach((m) => {
      map.set(String(m.kommunenr), m.landsdel);
    });
    this.kommuneToLandsdelMap = map;
    return map;
  }

  private static buildPeriodTemplate(fromDate: Date, toDate: Date, interval: JPBIndexInterval): PeriodTemplate[] {
    const periods: PeriodTemplate[] = [];
    let cursor = this.toPeriodStart(fromDate, interval);
    const toMs = toDate.getTime();

    while (cursor.getTime() <= toMs) {
      const start = new Date(cursor);
      const next = this.advancePeriod(start, interval);
      const end = new Date(next.getTime() - 1);
      periods.push({
        key: this.toPeriodKey(start, interval),
        start: start.getTime(),
        end: end.getTime(),
      });
      cursor = next;
    }

    return periods;
  }

  private static toPeriodStart(date: Date, interval: JPBIndexInterval): Date {
    const year = date.getFullYear();
    const month = date.getMonth();

    if (interval === "quarter") {
      const quarterStartMonth = Math.floor(month / 3) * 3;
      return new Date(year, quarterStartMonth, 1, 0, 0, 0, 0);
    }

    if (interval === "halfYear") {
      return new Date(year, month < 6 ? 0 : 6, 1, 0, 0, 0, 0);
    }

    return new Date(year, 0, 1, 0, 0, 0, 0);
  }

  private static advancePeriod(startDate: Date, interval: JPBIndexInterval): Date {
    if (interval === "quarter") return new Date(startDate.getFullYear(), startDate.getMonth() + 3, 1, 0, 0, 0, 0);
    if (interval === "halfYear") return new Date(startDate.getFullYear(), startDate.getMonth() + 6, 1, 0, 0, 0, 0);
    return new Date(startDate.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
  }

  private static toPeriodKey(date: Date, interval: JPBIndexInterval): string {
    const year = date.getFullYear();
    if (interval === "quarter") {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${year}K${quarter}`;
    }
    if (interval === "halfYear") {
      const half = date.getMonth() < 6 ? 1 : 2;
      return `${year}H${half}`;
    }
    return `${year}`;
  }

  private static calculateStatistic(values: number[], statistic: JPBIndexStatistic): number | null {
    if (!values.length) return null;
    if (statistic === "mean") {
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    if (n % 2 === 1) return sorted[(n - 1) / 2];
    return (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
  }

  private static getValuesForSmoothingMode(
    periodTemplate: PeriodTemplate[],
    groupedValues: Map<string, number[]>,
    idx: number,
    smoothingMode: JPBIndexSmoothingMode
  ): number[] {
    const current = groupedValues.get(periodTemplate[idx].key) || [];
    if (smoothingMode === "none") {
      return current;
    }

    // Sliding window (t-1, t, t+1): use transaction-level pooling, not median-of-medians.
    const prev = idx > 0 ? groupedValues.get(periodTemplate[idx - 1].key) || [] : [];
    const next = idx < periodTemplate.length - 1 ? groupedValues.get(periodTemplate[idx + 1].key) || [] : [];
    return [...prev, ...current, ...next];
  }

  private static groupRowsByLandsdel(rows: JPBRawTransactionRow[]): Map<string, JPBRawTransactionRow[]> {
    const grouped = new Map<string, JPBRawTransactionRow[]>();
    rows.forEach((row) => {
      const list = grouped.get(row.landsdel);
      if (list) {
        list.push(row);
      } else {
        grouped.set(row.landsdel, [row]);
      }
    });
    return grouped;
  }

  private static buildDocumentFromRows(
    landsdel: string,
    rows: JPBRawTransactionRow[],
    fromTimestamp: number,
    toTimestamp: number,
    options: JPBIndexBuilderOptions
  ): JPBIndexDocument {
    const fromDate = new Date(fromTimestamp);
    const toDate = new Date(toTimestamp);
    const periodTemplate = this.buildPeriodTemplate(fromDate, toDate, options.interval);
    const groupedValues = new Map<string, number[]>();

    rows.forEach((row) => {
      const value = this.toPricePerSquareMeter(row, options.buildingAreaThresholdHa, options.subtractBuildingPrices);
      if (value === null) {
        return;
      }
      const dt = new Date(row.dato);
      const key = this.toPeriodKey(dt, options.interval);
      const existing = groupedValues.get(key);
      if (existing) {
        existing.push(value);
      } else {
        groupedValues.set(key, [value]);
      }
    });

    const periods: JPBIndexPeriod[] = periodTemplate.map((period, idx) => {
      const values = this.getValuesForSmoothingMode(periodTemplate, groupedValues, idx, options.smoothingMode);
      const value = this.calculateStatistic(values, options.statistic);
      return {
        periodKey: period.key,
        periodStart: period.start,
        periodEnd: period.end,
        value,
        factor: null,
        txCount: values.length,
      };
    });

    const basePeriod = periods.find((p) => typeof p.value === "number" && p.value > 0) || null;
    const baseValue = basePeriod?.value ?? null;
    const periodsWithFactor = periods.map((period) => {
      if (baseValue && period.value && baseValue > 0) {
        return { ...period, factor: period.value / baseValue };
      }
      return { ...period, factor: null };
    });

    const doc: JPBIndexDocument = {
      landsdel,
      interval: options.interval,
      statistic: options.statistic,
      smoothingMode: options.smoothingMode,
      buildingAreaThresholdHa: Math.max(0, Number(options.buildingAreaThresholdHa) || 0),
      subtractBuildingPrices: Boolean(options.subtractBuildingPrices),
      yearsBack: Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK)),
      generatedAt: Date.now(),
      basePeriod: basePeriod?.periodKey ?? null,
      periods: periodsWithFactor,
    };

    if (options.includeFit) {
      const fit = this.computeExponentialFit(periodsWithFactor);
      if (fit) {
        doc.fitExp = fit;
      }
    }

    return doc;
  }

  private static toPricePerSquareMeter(
    row: JPBRawTransactionRow,
    buildingAreaThresholdHa: number,
    subtractBuildingPrices: boolean
  ): number | null {
    if (!(row.m2total > 0) || !(row.pris > 0)) {
      return null;
    }

    const hasBuildingValue =
      (typeof row.prisDriftsbygning === "number" && row.prisDriftsbygning > 0) ||
      (typeof row.prisStuehus === "number" && row.prisStuehus > 0);
    const thresholdM2 = Math.max(0, Number(buildingAreaThresholdHa) || 0) * 10000;
    const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || row.m2total >= thresholdM2;
    if (!buildingAreaOk) {
      return null;
    }

    const buildingsPriceSum = (row.prisDriftsbygning ?? 0) + (row.prisStuehus ?? 0);
    const effectiveRawPrice = subtractBuildingPrices ? Math.max(0, row.pris - buildingsPriceSum) : row.pris;
    if (!(effectiveRawPrice > 0)) {
      return null;
    }

    return effectiveRawPrice / row.m2total;
  }

  private static async getOrFetchRawRows(
    yearsBack: number,
    forceRefresh: boolean
  ): Promise<{ rows: JPBRawTransactionRow[]; cache: JPBRowsCache; usedCache: boolean }> {
    const sourceIndexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");
    const { date: referenceDate, fromMetadata } = await this.getReferenceDateInfo();
    const referenceDateMs = referenceDate.getTime();

    if (
      !forceRefresh &&
      this.rowsCache &&
      this.rowsCache.sourceIndexName === sourceIndexName &&
      this.rowsCache.yearsBack === yearsBack &&
      (!fromMetadata || this.rowsCache.referenceDateMs === referenceDateMs)
    ) {
      return { rows: this.rowsCache.rows, cache: { ...this.rowsCache }, usedCache: true };
    }

    const fromDate = new Date(referenceDateMs);
    fromDate.setFullYear(fromDate.getFullYear() - yearsBack);
    const fromTimestamp = fromDate.getTime();
    const toTimestamp = referenceDateMs;

    const rows = await this.fetchRawRowsInDateRange(sourceIndexName, fromTimestamp, toTimestamp);
    this.rowsCache = {
      rows,
      sourceIndexName,
      yearsBack,
      referenceDateMs,
      referenceFromMetadata: fromMetadata,
      fromTimestamp,
      toTimestamp,
      fetchedAt: Date.now(),
    };
    return { rows, cache: { ...this.rowsCache }, usedCache: false };
  }

  private static toCacheInfo(cache: JPBRowsCache, usedCache: boolean): JPBIndexBuilderCacheInfo {
    return {
      usedCache,
      sourceIndexName: cache.sourceIndexName,
      yearsBack: cache.yearsBack,
      referenceDateMs: cache.referenceDateMs,
      rowsInMemory: cache.rows.length,
      fromTimestamp: cache.fromTimestamp,
      toTimestamp: cache.toTimestamp,
      fetchedAt: cache.fetchedAt,
    };
  }

  private static async fetchRawRowsInDateRange(
    sourceIndexName: string,
    fromTimestamp: number,
    toTimestamp: number
  ): Promise<JPBRawTransactionRow[]> {
    const fromSeconds = Math.floor(fromTimestamp / 1000);
    const toSeconds = Math.floor(toTimestamp / 1000);
    const configuredFetchSize = Number(SettingsManager.getSystemSetting("jpb.indexBuilderFetchSize", DEFAULT_FETCH_SIZE));
    const fetchSize = Math.max(1000, Math.min(MAX_FETCH_SIZE, Number.isFinite(configuredFetchSize) ? configuredFetchSize : DEFAULT_FETCH_SIZE));
    const sourceEs = new ESInterfaceJWTAuth(sourceIndexName);
    const kommuneToLandsdel = this.getKommuneToLandsdelMap();
    const allRows: JPBRawTransactionRow[] = [];
    let page = 0;
    let searchAfter: unknown[] | undefined;
    let paginationRequired = false;

    while (page < MAX_PAGES) {
      page += 1;
      const query: any = {
        size: fetchSize,
        sort: [{ dato: "asc" }, { _seq_no: "asc" }],
        _source: ["dato", "pris", "m2total", "komkode", "prisDriftsbygning", "prisStuehus"],
        query: {
          bool: {
            filter: [
              {
                bool: {
                  should: [
                    {
                      range: {
                        dato: {
                          gte: fromTimestamp,
                          lte: toTimestamp,
                        },
                      },
                    },
                    {
                      range: {
                        dato: {
                          gte: fromSeconds,
                          lte: toSeconds,
                        },
                      },
                    },
                  ],
                  minimum_should_match: 1,
                },
              },
            ],
          },
        },
      };

      if (searchAfter && searchAfter.length > 0) {
        query.search_after = searchAfter;
      }

      const response = await sourceEs.doQuery(query);
      const hits = (response?.hits?.hits || []) as JPBHit[];
      if (!hits.length) {
        break;
      }

      hits.forEach((hit) => {
        const src = hit._source;
        const rawDato = src?.dato;
        let dato = typeof rawDato === "number" ? rawDato : NaN;
        if (Number.isFinite(dato) && dato > 0 && dato < 10_000_000_000) {
          dato *= 1000;
        }
        const price = src?.pris;
        const area = src?.m2total;
        const normalizedKommune = this.normalizeKommuneCode(src?.komkode);
        const landsdel = normalizedKommune ? kommuneToLandsdel.get(normalizedKommune) : undefined;
        if (
          !landsdel ||
          typeof price !== "number" ||
          !(price > 0) ||
          typeof area !== "number" ||
          !(area > 0) ||
          typeof dato !== "number" ||
          !(dato > 0)
        ) {
          return;
        }

        allRows.push({
          dato,
          pris: price,
          m2total: area,
          komkode: normalizedKommune!,
          landsdel,
          prisDriftsbygning: this.toPositiveNumberOrNull(src?.prisDriftsbygning),
          prisStuehus: this.toPositiveNumberOrNull(src?.prisStuehus),
        });
      });

      if (hits.length < fetchSize) {
        break;
      }
      paginationRequired = true;

      const lastHit = hits[hits.length - 1];
      if (lastHit?.sort && lastHit.sort.length > 0) {
        searchAfter = lastHit.sort;
      } else if (lastHit?._source?.dato && typeof lastHit._seq_no === "number") {
        searchAfter = [lastHit._source.dato, lastHit._seq_no];
      } else {
        break;
      }
    }

    if (paginationRequired && allRows.length > fetchSize) {
      console.info(`JPBIndexBuilder: paginated all-landsdele fetch used, rows=${allRows.length}`);
    }

    return allRows;
  }

  private static toPositiveNumberOrNull(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
  }

  private static normalizeKommuneCode(value: unknown): string | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(Math.trunc(value));
    }
    if (typeof value === "string") {
      const parsed = Number(value.trim());
      if (Number.isFinite(parsed)) {
        return String(Math.trunc(parsed));
      }
    }
    return undefined;
  }
}

